/**
 * src/components/ejecuciones/ResumenSemanal.jsx
 * El registro de envíos del correo semanal.
 *
 * Vivía dentro de `pages/Actividad.jsx`, que se retiró al fundir Actividad y
 * Programación en Ejecuciones (fase 3).
 *
 * ⚠️ Se conserva ENTERO a propósito. En el mockup escribí que este bloque
 * «deja de ser una tarjeta suelta porque es un job programado y le basta una
 * fila con su acción Forzar». Al mirarlo de cerca era falso: la fila del job
 * sí cabe en la línea de tiempo, pero el REGISTRO —una fila por (tenant,
 * semana) con su estado, y quién no lo recibió— no cabe en ninguna fila, y es
 * lo único que responde a «¿salió bien el viernes, y para quién no?».
 * Sacrificarlo habría hecho la historia más limpia y el panel peor.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { Pildora } from '../ui/Pildora'
import {
  listarEnvios, ejecutarResumen, vistaPreviaResumen, enviarPruebaResumen,
} from '../../api/resumenSemanal'
import { BloqueJSON } from '../ui/BloqueJSON'

// ── Resumen semanal por email ────────────────────────────────────────────────
//
// El correo sale solo los viernes a las 12:00 de Chile. Este bloque existe para
// los dos casos en que esperar no sirve: que el disparo programado no llegara a
// ocurrir (web caído más de las 6 h de gracia, y entonces no hay ni fila), y que
// fallara para un tenant concreto, cuya semana queda cerrada y sin reenvío
// posible salvo forzándola.

const ESTADO_COLOR = {
  ok: 'var(--color-ok)',
  parcial: 'var(--color-running)',
  enviando: 'var(--color-running)',
  error: 'var(--color-error)',
  duplicado: 'var(--color-text-3)',
  sin_datos: 'var(--color-text-3)',
  sin_destinatarios: 'var(--color-text-3)',
  en_curso: 'var(--color-running)',
  omitido: 'var(--color-text-3)',
}

// Los dos botones de fila. Discretos y con etiqueta corta: comparten celda para
// no ensanchar una tabla que ya tiene seis columnas (ver el comentario en la celda).
const _ACCION = {
  background: 'none', border: '1px solid var(--color-border-2)',
  borderRadius: 'var(--radius-sm)', padding: '1px 8px', cursor: 'pointer',
  fontSize: 'var(--text-micro)', fontFamily: 'var(--font-mono)',
  color: 'var(--color-text-2)', whiteSpace: 'nowrap',
}

// Filtros rápidos por estado (Manuel, 28-08). «Con problema» agrupa lo que
// pide acción; «sin destinatarios» es la lista de gestiones: pedir el correo.
const FILTROS_ESTADO = [
  { clave: null, etiqueta: 'todos' },
  { clave: 'ok', etiqueta: 'ok' },
  { clave: 'problema', etiqueta: 'con problema' },
  { clave: 'sin_destinatarios', etiqueta: 'sin destinatarios' },
]
const ESTADOS_PROBLEMA = new Set(['error', 'parcial', 'enviando', 'en_curso'])

function _valorDeOrden(f, campo) {
  if (campo === 'enviados') return f.enviados ?? -1
  if (campo === 'direcciones') return (f.destinos || []).map(d => d.email).join(',')
  return f[campo] ?? ''
}

export function ResumenSemanal() {
  const navigate = useNavigate()
  const [envios, setEnvios] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [lanzando, setLanzando] = useState(false)
  const [tenant, setTenant] = useState('')
  const [resultado, setResultado] = useState(null)
  const [filtroEstado, setFiltroEstado] = useState(null)
  const [filtroSemana, setFiltroSemana] = useState('')
  // Orden por columna: clic alterna asc/desc. Por defecto, lo del servidor
  // (las semanas más recientes arriba).
  const [orden, setOrden] = useState({ campo: null, asc: true })
  // ── Vista previa y envío de prueba (24-09-2026, encargo de otro hilo) ──────
  //
  // 🔑 POR QUÉ EXISTEN ESTOS DOS BOTONES. Los endpoints `GET /vista-previa` y
  // `POST /prueba` llevaban meses funcionando y sin botón en ninguna pantalla, y
  // `get_current_admin` exige cabecera: pegar la URL en el navegador da 401. O
  // sea que solo se podían usar con `curl` y el ADMIN_TOKEN — y quien los
  // necesita es Manuel, que no trabaja en terminal. Es la regla 8 bis-2 literal:
  // si para hacer algo hay que salirse del panel, el trabajo es arreglar el panel.
  const [previa, setPrevia] = useState(null)       // {clienteId, semanaFila, html}
  const [cargandoPrevia, setCargandoPrevia] = useState(null)  // clienteId en curso
  const [errorPrevia, setErrorPrevia] = useState(null)
  // `prueba` guarda la intención ANTES de mandar: el segundo paso enseña a qué
  // dirección va y de qué tenant son los datos. Ver `_confirmarPrueba`.
  const [prueba, setPrueba] = useState(null)       // {clienteId, email, confirmado}
  const [enviandoPrueba, setEnviandoPrueba] = useState(false)
  const [resultadoPrueba, setResultadoPrueba] = useState(null)
  const [errorPrueba, setErrorPrueba] = useState(null)

  async function abrirPrevia(fila) {
    setErrorPrevia(null)
    setCargandoPrevia(fila.cliente_id)
    try {
      const html = await vistaPreviaResumen(fila.cliente_id)
      setPrevia({ clienteId: fila.cliente_id, semanaFila: fila.semana, html })
    } catch (e) {
      setErrorPrevia(`${fila.cliente_id}: ${e?.response?.data?.detail || e.message}`)
      setPrevia(null)
    } finally {
      setCargandoPrevia(null)
    }
  }

  async function confirmarPrueba() {
    if (!prueba?.clienteId || !prueba?.email) return
    setEnviandoPrueba(true)
    setErrorPrueba(null)
    try {
      setResultadoPrueba(await enviarPruebaResumen(prueba.clienteId, prueba.email))
      setPrueba(null)
    } catch (e) {
      setErrorPrueba(e?.response?.data?.detail || e.message)
    } finally {
      setEnviandoPrueba(false)
    }
  }

  const cargar = async () => {
    setCargando(true)
    try {
      setEnvios(await listarEnvios({ limite: 80 }))
    } catch (e) {
      setResultado({ error: e.response?.data?.detail || 'No se pudo leer el registro.' })
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => { cargar() }, [])

  const lanzar = async (opts) => {
    setLanzando(true)
    setResultado(null)
    try {
      setResultado(await ejecutarResumen(opts))
      await cargar()
    } catch (e) {
      setResultado({ error: e.response?.data?.detail || 'No se pudo lanzar el envío.' })
    } finally {
      setLanzando(false)
    }
  }

  const todas = envios?.envios || []
  const semanas = useMemo(
    () => [...new Set(todas.map(f => f.semana).filter(Boolean))].sort().reverse(),
    [todas])

  const filas = useMemo(() => {
    let out = todas
    if (filtroEstado === 'ok') out = out.filter(f => f.estado === 'ok')
    else if (filtroEstado === 'problema') out = out.filter(f => ESTADOS_PROBLEMA.has(f.estado))
    else if (filtroEstado === 'sin_destinatarios') {
      // También el respaldo del contacto: son los tenants sin usuario de verdad.
      out = out.filter(f => f.estado === 'sin_destinatarios'
        || (f.detalle || '').startsWith('sin usuarios'))
    }
    if (filtroSemana) out = out.filter(f => f.semana === filtroSemana)
    if (orden.campo) {
      out = [...out].sort((a, b) => {
        const va = _valorDeOrden(a, orden.campo), vb = _valorDeOrden(b, orden.campo)
        const c = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb))
        return orden.asc ? c : -c
      })
    }
    return out
  }, [todas, filtroEstado, filtroSemana, orden])

  const ordenar = campo => setOrden(o =>
    o.campo === campo ? { campo, asc: !o.asc } : { campo, asc: true })

  return (
    <Card
      title="Resumen semanal por email"
      subtitle="Sale solo los viernes 12:00 (Chile). Aquí se ve si salió, y se relanza si no."
      action={
        <Button variant="secondary" size="sm" onClick={cargar} loading={cargando}>
          Refrescar
        </Button>
      }
    >
      <div style={{
        display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap',
        alignItems: 'center', marginBottom: 'var(--space-4)',
      }}>
        {FILTROS_ESTADO.map(f => (
          <Pildora key={f.etiqueta} activa={filtroEstado === f.clave}
                   onClick={() => setFiltroEstado(f.clave)}>
            {f.etiqueta}
          </Pildora>
        ))}
        <select
          value={filtroSemana}
          onChange={e => setFiltroSemana(e.target.value)}
          style={{
            marginLeft: 'var(--space-2)', padding: '4px 8px',
            fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)',
            borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)',
            background: 'var(--color-surface)', color: 'var(--color-text-2)',
          }}
        >
          <option value="">todas las semanas</option>
          {semanas.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {filas.length === 0 && !cargando && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)', marginBottom: 'var(--space-4)' }}>
          {todas.length === 0
            ? 'Todavía no hay ningún envío registrado.'
            : 'Ningún envío coincide con el filtro.'}
        </div>
      )}

      {filas.length > 0 && (
        <div style={{ marginBottom: '18px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-3)' }}>
                {[['cliente_id', 'Tenant'], ['semana', 'Semana'], ['estado', 'Estado'],
                  ['enviados', 'Enviados'], ['direcciones', 'Direcciones']].map(([campo, rotulo]) => (
                  <th key={campo}
                      onClick={() => ordenar(campo)}
                      style={{ padding: '6px 8px', fontWeight: 500, cursor: 'pointer',
                               userSelect: 'none', whiteSpace: 'nowrap' }}>
                    {rotulo}
                    <span style={{ display: 'inline-block', width: '12px',
                                   color: 'var(--color-text-3)' }}>
                      {orden.campo === campo ? (orden.asc ? '↑' : '↓') : ''}
                    </span>
                  </th>
                ))}
                <th style={{ padding: '6px 8px', fontWeight: 500 }}>Detalle</th>
                <th style={{ padding: '6px 8px', fontWeight: 500 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filas.map(f => {
                const sinDestinatarios = f.estado === 'sin_destinatarios'
                  || (f.detalle || '').startsWith('sin usuarios')
                return (
                <tr key={f.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <button
                      onClick={() => navigate(`/clientes/${f.cliente_id}`)}
                      title="Abrir la ficha del cliente"
                      style={{
                        background: 'none', border: 'none', padding: 0,
                        fontFamily: 'var(--font-mono)', fontSize: 'inherit',
                        color: 'var(--color-text)', cursor: 'pointer',
                        textDecoration: 'underline',
                        textDecorationColor: 'var(--color-border-2)',
                        textUnderlineOffset: '3px',
                      }}
                    >
                      {f.cliente_id}
                    </button>
                  </td>
                  <td style={{ padding: '6px 8px', fontFamily: 'var(--font-mono)',
                               whiteSpace: 'nowrap' }}>{f.semana}</td>
                  <td style={{ padding: '6px 8px', color: ESTADO_COLOR[f.estado] || 'var(--color-text-2)' }}>
                    {f.estado}
                  </td>
                  <td style={{ padding: '6px 8px', fontFamily: 'var(--font-mono)' }}>
                    {f.enviados}/{f.destinatarios}
                  </td>
                  {/* A quién se escribió. Sale del cruce con `email_envios` por
                      (tenant, semana): la dirección de cada mensaje vive allí, y
                      duplicarla en el registro semanal habría creado un segundo
                      sitio con dato personal. Si un titular ejerció su supresión,
                      sus filas ya no están y aquí deja de constar — correcto. */}
                  <td style={{ padding: '6px 8px', fontFamily: 'var(--font-mono)',
                               fontSize: 11, color: 'var(--color-text-2)', maxWidth: 300,
                               overflowWrap: 'anywhere' }}>
                    {(f.destinos || []).length
                      ? f.destinos.map(d => (
                          <div key={d.email} title={`estado del mensaje: ${d.estado}`}>
                            {d.email}
                            {d.estado && d.estado !== 'enviado' && (
                              <span style={{ color: 'var(--color-text-3)' }}> · {d.estado}</span>
                            )}
                          </div>
                        ))
                      : <span style={{ color: 'var(--color-text-3)' }}>
                          {f.destinatarios ? 'no consta' : '—'}
                        </span>}
                  </td>
                  <td style={{ padding: '6px 8px', color: 'var(--color-text-3)' }}>
                    {/* El aviso accionable (Manuel, 28-08): un tenant sin usuarios
                        destinatarios no es un estado neutro, es una gestión
                        pendiente — pedirle el correo y darlo de alta. */}
                    {sinDestinatarios && (
                      <span style={{
                        display: 'inline-block', marginRight: 'var(--space-2)',
                        padding: '1px 8px', borderRadius: 'var(--radius-pill)',
                        fontSize: 'var(--text-micro)', fontFamily: 'var(--font-mono)',
                        color: 'var(--color-warn)', border: '1px solid var(--color-warn)',
                        whiteSpace: 'nowrap',
                      }}>
                        ⚠ faltan destinatarios — pedir al cliente
                      </span>
                    )}
                    {f.detalle || (sinDestinatarios ? '' : '—')}
                  </td>
                  {/* ⚠️ UNA sola celda para las dos acciones, y con las etiquetas
                      cortas, a propósito: esta tabla ya tiene seis columnas dentro
                      de un recuadro con `overflowX: auto`, y dos columnas más la
                      empujarían a esconder la última tras el scroll. Es el fallo
                      que este hilo acaba de arreglar en el Embudo el 24-09; hay
                      guardián de desborde para esta tabla por el mismo motivo. */}
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => abrirPrevia(f)}
                      disabled={cargandoPrevia === f.cliente_id}
                      title={`Ver el correo de ${f.cliente_id} tal como saldría HOY, sin enviar nada`}
                      style={_ACCION}
                    >
                      {cargandoPrevia === f.cliente_id ? '…' : 'Vista previa'}
                    </button>
                    <button
                      onClick={() => {
                        setResultadoPrueba(null); setErrorPrueba(null)
                        setPrueba({ clienteId: f.cliente_id, email: '', confirmado: false })
                      }}
                      title={`Enviar el resumen de ${f.cliente_id} a una dirección concreta`}
                      style={{ ..._ACCION, marginLeft: 6 }}
                    >
                      Prueba
                    </button>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <Button onClick={() => lanzar({})} loading={lanzando}>
        Lanzar ahora (todos los tenants)
      </Button>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', marginTop: 'var(--space-2)' }}>
        Respeta el candado: a quien ya recibió su resumen esta semana no se le vuelve
        a escribir. Es la salida cuando el viernes no llegó a dispararse.
      </div>

      <details style={{ marginTop: 'var(--space-4)' }}>
        <summary style={{ cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>
          Reenviar a un tenant (semana ya cerrada)
        </summary>
        <div style={{ marginTop: '10px', padding: 'var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)', marginBottom: '10px' }}>
            Vuelve a mandar el resumen de esta semana a <strong>todos los usuarios</strong> de
            ese tenant, aunque ya conste enviado. Para el tenant que falló el viernes.
            No se puede hacer en bloque a propósito: la recuperación es tenant a tenant.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              value={tenant}
              onChange={e => setTenant(e.target.value)}
              placeholder="cliente_id"
              style={{
                flex: 1, padding: '6px 10px', fontSize: 'var(--text-sm)',
                fontFamily: 'var(--font-mono)', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface-2)', color: 'var(--color-text)',
              }}
            />
            <Button
              variant="danger" size="sm" loading={lanzando}
              disabled={!tenant.trim()}
              onClick={() => lanzar({ clienteId: tenant.trim(), forzar: true })}
            >
              Reenviar
            </Button>
          </div>
        </div>
      </details>

      {resultado?.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>
          ✗ {resultado.error}
        </div>
      )}
      {resultado && !resultado.error && (
        <BloqueJSON datos={resultado} nombre="reenvio-resumen" maxAlto="300px" />
      )}

      {errorPrevia && (
        <div style={{ marginTop: 'var(--space-3)', color: 'var(--color-error)',
                      fontSize: 'var(--text-sm)' }}>
          No se pudo construir la vista previa — {errorPrevia}
        </div>
      )}

      {previa && (
        <div style={{ marginTop: 'var(--space-4)', border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)',
            padding: 'var(--space-2) var(--space-3)', background: 'var(--color-surface-2)',
            borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap',
          }}>
            <strong style={{ fontSize: 'var(--text-sm)' }}>
              Vista previa · {previa.clienteId}
            </strong>
            {/* ⚠️ LO QUE ESTO ES, DICHO DONDE SE LEE. El endpoint no acepta semana:
                construye el informe de AHORA. El botón vive en la fila de una
                semana concreta, así que sin esta línea el panel afirmaría por
                colocación que enseña el correo de aquella semana — y enseña el de
                hoy. Es la misma clase de mentira que un rótulo obsoleto. */}
            <span style={{ fontSize: 'var(--text-micro)', color: 'var(--color-warn)',
                           fontFamily: 'var(--font-mono)' }}>
              el correo tal como saldría HOY — no el de {previa.semanaFila}
            </span>
            <span style={{ fontSize: 'var(--text-micro)', color: 'var(--color-text-3)' }}>
              no se ha enviado nada
            </span>
            <button onClick={() => setPrevia(null)}
                    style={{ ..._ACCION, marginLeft: 'auto' }}>cerrar</button>
          </div>
          {/* 🔒 `iframe` con `sandbox` VACÍO y `srcDoc`, decidido por Manuel el
              24-09. Dos motivos, y ninguno es adorno:
                · aislamiento de estilos — es el HTML de un correo, con sus propias
                  reglas; inyectarlo en la página se llevaría por delante el panel;
                · aislamiento de ejecución — `sandbox=""` sin `allow-scripts` ni
                  `allow-same-origin` deja el documento sin scripts y sin acceso al
                  origen del panel, donde vive el token de sesión.
              `srcDoc` y no `src`: pedir la URL otra vez desde el iframe iría sin la
              cabecera `Authorization` y devolvería 401. */}
          <iframe
            title={`Correo semanal de ${previa.clienteId}`}
            srcDoc={previa.html}
            sandbox=""
            style={{ width: '100%', height: 620, border: 'none',
                     background: 'var(--color-bg)' }}
          />
        </div>
      )}

      {prueba && (
        <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)',
                      border: '1px solid var(--color-warn)',
                      borderRadius: 'var(--radius-md)' }}>
          <div style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>
            <strong>Enviar el resumen de <code>{prueba.clienteId}</code> a una
            dirección</strong>
          </div>
          {/* 🔴 El envío de prueba ESCRIBE A UNA PERSONA. No toca el registro de la
              semana ni la huella de precios, así que no deja al tenant sin su
              correo del viernes — pero el mensaje sale de verdad, y un clic de más
              le llega a alguien. De ahí los dos pasos: el primero recoge la
              dirección, el segundo la enseña escrita antes de mandar. */}
          <div style={{ fontSize: 'var(--text-micro)', color: 'var(--color-text-3)',
                        marginBottom: 'var(--space-2)' }}>
            Sale un correo de verdad. No cuenta como el resumen semanal del tenant:
            no toca su registro de la semana ni la huella de precios.
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap',
                        alignItems: 'center' }}>
            <input
              id="prueba-email"
              type="email"
              value={prueba.email}
              placeholder="dirección de destino"
              onChange={e => setPrueba({ ...prueba, email: e.target.value,
                                         confirmado: false })}
              style={{
                padding: '5px 10px', minWidth: 280,
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
                color: 'var(--color-text)', fontSize: 'var(--text-sm)',
                fontFamily: 'var(--font-mono)',
              }}
            />
            {!prueba.confirmado ? (
              <Button size="sm" variant="secondary"
                      disabled={!prueba.email.includes('@')}
                      onClick={() => setPrueba({ ...prueba, confirmado: true })}>
                Continuar
              </Button>
            ) : (
              <Button size="sm" loading={enviandoPrueba} onClick={confirmarPrueba}>
                Enviar a {prueba.email}
              </Button>
            )}
            <button style={_ACCION} onClick={() => setPrueba(null)}>cancelar</button>
          </div>
          {prueba.confirmado && (
            <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)',
                          color: 'var(--color-warn)' }}>
              Va a salir un correo con los datos de <code>{prueba.clienteId}</code>{' '}
              a <code>{prueba.email}</code>. Pulse «Enviar» para mandarlo.
            </div>
          )}
        </div>
      )}

      {errorPrueba && (
        <div style={{ marginTop: 'var(--space-3)', color: 'var(--color-error)',
                      fontSize: 'var(--text-sm)' }}>
          El envío de prueba falló — {errorPrueba}
        </div>
      )}
      {resultadoPrueba && (
        <BloqueJSON datos={resultadoPrueba} nombre="prueba-resumen" maxAlto="300px" />
      )}
    </Card>
  )
}
